import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '@library/providers/ApiClientProvider';
import { Section } from '@/components/ui';

/*
 * The pricing section.
 *
 * Prices are BOTH hardcoded below and fetched from GET /defaults/plans, on
 * purpose. The hardcoded set renders on first paint, so a marketing page never
 * shows a spinner where the price should be; the fetch then overlays whatever the
 * database actually says, so a price change reaches this page without a deploy.
 * If the API is unreachable the page still reads correctly — one deploy behind at
 * worst, which is the failure mode this section had permanently before.
 *
 * There is no free tier. TRIAL is the entry point and is listed as a plan,
 * because that is now what a new account gets and keeps until it pays. PlanID 4
 * (the old Unlimited) is retired and the endpoint does not return it.
 */

type PlanKey = 'trial' | 'plus' | 'enterprise' | 'company';

interface PlanRow {
    key: PlanKey;
    planID: number;
    price: number | null;   // null = priced per package (company)
    featured: boolean;
}

const PLANS: readonly PlanRow[] = [
    { key: 'trial',      planID: 2, price: 0,    featured: false },
    { key: 'plus',       planID: 3, price: 4.99, featured: true  },
    { key: 'enterprise', planID: 5, price: 9.99, featured: false },
    { key: 'company',    planID: 6, price: null, featured: false },
];

// Which bullet lines each plan shows. Keyed per plan because "5 routes" and
// "unlimited routes" are different sentences, not one sentence with a number in it.
const PLAN_POINTS: Record<PlanKey, readonly string[]> = {
    trial:      ['routes', 'schedules', 'markers', 'paths'],
    plus:       ['routes', 'schedules', 'markers', 'paths', 'offline', 'corrections', 'share'],
    enterprise: ['everything', 'offline', 'corrections', 'share'],
    // 'publish' dropped: public publishing is being removed
    // (busLineApi/docs/PLAN-unpublish-and-report.md), so the plan card should not
    // lead with it. 'tracking' -- a live map of every driver -- is dropped for a
    // different reason: the feature is gone (attic/tracking/), and a plan card is
    // not the place to advertise something the product no longer does.
    company:    ['everything', 'seats', 'drivers', 'offline', 'corrections'],
};

// The company seat packages, same both-and treatment as the prices above.
const FALLBACK_TIERS = [
    { TierID: 1, TierName: 'up to 5',    MaxSeats: 5,    PriceMonthly: '49.00' },
    { TierID: 2, TierName: 'up to 10',   MaxSeats: 10,   PriceMonthly: '89.00' },
    { TierID: 3, TierName: 'up to 20',   MaxSeats: 20,   PriceMonthly: '169.00' },
    { TierID: 4, TierName: 'up to 30',   MaxSeats: 30,   PriceMonthly: '239.00' },
    { TierID: 5, TierName: 'on request', MaxSeats: null, PriceMonthly: '0.00' },
];

interface CatalogPlan {
    PlanID: number;
    PriceMonthly: string;
}
interface CatalogTier {
    TierID: number;
    TierName: string;
    MaxSeats: number | null;
    PriceMonthly: string;
}

const formatPrice = (price: number, lang: string): string =>
    new Intl.NumberFormat(lang === 'po' ? 'pl' : lang, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
    }).format(price);

export const Plans: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { get } = useApiClient();

    const [livePrices, setLivePrices] = useState<Record<number, number> | null>(null);
    const [tiers, setTiers] = useState<CatalogTier[]>(FALLBACK_TIERS);

    useEffect(() => {
        let cancelled = false;
        void get<{ plans: CatalogPlan[]; seatTiers: CatalogTier[] }>('/api/v1/defaults/plans')
            .then((res) => {
                if (cancelled || !res.success || !res.data) return;
                const prices: Record<number, number> = {};
                (res.data.plans ?? []).forEach((p) => { prices[p.PlanID] = Number(p.PriceMonthly); });
                setLivePrices(prices);
                if (res.data.seatTiers?.length) setTiers(res.data.seatTiers);
            })
            // A failed price fetch is not worth surfacing on a marketing page —
            // the hardcoded set above is already rendered and correct enough.
            .catch(() => undefined);
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const priceOf = (plan: PlanRow): number | null =>
        livePrices?.[plan.planID] ?? plan.price;

    // Company pricing is a range, so the card leads with the cheapest package.
    const companyFrom = (): number => {
        const priced = tiers.filter((tr) => tr.MaxSeats !== null).map((tr) => Number(tr.PriceMonthly));
        return priced.length ? Math.min(...priced) : 49;
    };

    // The biggest capped package, so the on-request row can say what "over" means
    // without hardcoding 30 — the packages are data.
    const largestPackage = (): number => {
        const caps = tiers.map((tr) => tr.MaxSeats).filter((n): n is number => n !== null);
        return caps.length ? Math.max(...caps) : 30;
    };

    return (
        <Section
            id="plans"
            eyebrow={t('web.plans.eyebrow')}
            title={t('web.plans.title')}
            sub={t('web.plans.sub')}
        >
            <div className="grid grid--4">
                {PLANS.map((plan) => {
                    const price = priceOf(plan);
                    return (
                        <article className={`plan${plan.featured ? ' plan--featured' : ''}`} key={plan.key}>
                            <div>
                                <div className="plan__name">
                                    {t(`web.plans.${plan.key}.name`)}
                                    {plan.featured ? (
                                        <span className="badge badge--primary" style={{ marginLeft: 8 }}>
                                            {t('web.plans.popular')}
                                        </span>
                                    ) : null}
                                </div>
                                <div className="plan__price">
                                    {plan.key === 'company'
                                        ? t('web.plans.fromPrice', { price: formatPrice(companyFrom(), i18n.language) })
                                        : price === 0
                                            ? t('web.plans.trialPrice')
                                            : formatPrice(price ?? 0, i18n.language)}
                                    {price === 0 && plan.key !== 'company'
                                        ? null
                                        : <small> / {t('web.plans.perMonth')}</small>}
                                </div>
                            </div>

                            <p className="plan__body">{t(`web.plans.${plan.key}.body`)}</p>

                            <ul className="plan__list">
                                {PLAN_POINTS[plan.key].map((point) => (
                                    <li key={point}>
                                        <span className="plan__tick" aria-hidden="true">✓</span>
                                        <span>{t(`web.plans.${plan.key}.points.${point}`)}</span>
                                    </li>
                                ))}
                            </ul>
                        </article>
                    );
                })}
            </div>

            {/* The company packages. A company buys capacity, so one figure on the
                card above would be a fiction — the table is the actual price list. */}
            <div className="plan" style={{ marginTop: 22 }}>
                <div className="plan__name">{t('web.plans.company.packagesTitle')}</div>
                <p className="plan__body">{t('web.plans.company.packagesSub')}</p>
                <ul className="plan__list">
                    {tiers.map((tr) => (
                        <li key={tr.TierID} style={{ justifyContent: 'space-between' }}>
                            {/* A null cap is the on-request package — "up to 0
                                drivers" is what interpolating it would produce. */}
                            <span>
                                {tr.MaxSeats === null
                                    ? t('web.plans.company.overLargest', { count: largestPackage() })
                                    : t('web.plans.company.upTo', { count: tr.MaxSeats })}
                            </span>
                            <strong>
                                {tr.MaxSeats === null
                                    ? t('web.plans.company.onRequest')
                                    : `${formatPrice(Number(tr.PriceMonthly), i18n.language)} / ${t('web.plans.perMonth')}`}
                            </strong>
                        </li>
                    ))}
                </ul>
            </div>

            <p className="section__sub" style={{ marginTop: 22 }}>{t('web.plans.footnote')}</p>
        </Section>
    );
};
